import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import {environment} from '../../../environments/environment';

export type LeadIntent = 'beta' | 'updates' | 'both';

export interface LeadCaptureState {
  isOpen: boolean;
  intent: LeadIntent;
  source: 'first-visit' | 'button';
}

export interface LeadEntry {
  email: string;
  intent: LeadIntent;
  createdAt: string;
}

@Injectable({
  providedIn: 'root'
})
export class LeadCaptureService {
  private readonly http = inject(HttpClient);
  private readonly noticeSeenKey = 'arcaneLegion.developmentNoticeSeen';
  private readonly leadsKey = 'arcaneLegion.leads';

  private readonly stateSubject = new BehaviorSubject<LeadCaptureState>({
    isOpen: false,
    intent: 'beta',
    source: 'first-visit'
  });

  readonly state$ = this.stateSubject.asObservable();

  showFirstVisitNotice(): void {
    if (!this.isBrowser()) {
      return;
    }

    const hasSeenNotice = window.localStorage.getItem(this.noticeSeenKey);

    if (!hasSeenNotice) {
      this.stateSubject.next({
        isOpen: true,
        intent: 'beta',
        source: 'first-visit'
      });
    }
  }

  openBetaSignup(): void {
    this.markNoticeSeen();

    this.stateSubject.next({
      isOpen: true,
      intent: 'beta',
      source: 'button'
    });
  }

  openUpdatesSignup(): void {
    this.markNoticeSeen();

    this.stateSubject.next({
      isOpen: true,
      intent: 'updates',
      source: 'button'
    });
  }

  close(): void {
    this.markNoticeSeen();

    this.stateSubject.next({
      ...this.stateSubject.value,
      isOpen: false
    });
  }

  async submit(email: string, intent: LeadIntent): Promise<{ success: boolean; message: string }> {
    const trimmedEmail = email.trim().toLowerCase();

    if (!this.isValidEmail(trimmedEmail)) {
      return {
        success: false,
        message: 'Please enter a valid email address.'
      };
    }

    try {
      await firstValueFrom(
        this.http.post(`${environment.apiBaseUrl}/leads`, {
          email: trimmedEmail,
          intent,
          source: 'web'
        })
      );
    } catch {
      // Keep the local fallback so the app still feels responsive during early dev.
      // The admin dashboard requires the API to be running to see leads across users.
    }

    if (this.isBrowser()) {
      this.saveLocalLead(trimmedEmail, intent);
      this.markNoticeSeen();
    }

    return {
      success: true,
      message: this.getSuccessMessage(intent)
    };
  }

  private saveLocalLead(email: string, intent: LeadIntent): void {
    const existingLeads = this.getStoredLeads();

    const alreadyExists = existingLeads.some(
      lead => lead.email === email && lead.intent === intent
    );

    if (alreadyExists) {
      return;
    }

    const newLead: LeadEntry = {
      email,
      intent,
      createdAt: new Date().toISOString()
    };

    window.localStorage.setItem(
      this.leadsKey,
      JSON.stringify([...existingLeads, newLead])
    );
  }

  private getSuccessMessage(intent: LeadIntent): string {
    if (intent === 'beta') {
      return 'You have been added to the Arcane Legion beta interest list.';
    }

    if (intent === 'updates') {
      return 'You have been subscribed to Arcane Legion updates.';
    }

    return 'You have been added to the beta list and subscribed to updates.';
  }

  private getStoredLeads(): LeadEntry[] {
    if (!this.isBrowser()) {
      return [];
    }

    const rawValue = window.localStorage.getItem(this.leadsKey);

    if (!rawValue) {
      return [];
    }

    try {
      return JSON.parse(rawValue) as LeadEntry[];
    } catch {
      return [];
    }
  }

  private markNoticeSeen(): void {
    if (!this.isBrowser()) {
      return;
    }

    window.localStorage.setItem(this.noticeSeenKey, 'true');
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  private isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
  }
}
